import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePageMeta } from '../../publicSite/usePageMeta';

export default function Register() {
  usePageMeta('Apply | School of Shem', 'Create a School of Shem student account and begin registration.');
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({
    fullName: '',
    surname: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (
      !form.fullName ||
      !form.surname ||
      !form.phone ||
      !form.email ||
      !form.password ||
      !form.confirmPassword
    ) {
      setError('Please complete all required fields.');
      return;
    }

    if (form.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await register({
        email: form.email.trim(),
        password: form.password,
        firstName: form.fullName.trim(),
        lastName: form.surname.trim(),
        role: 'student',
        profile: {
          phone: form.phone.trim(),
          paymentDue: false,
          pendingApproval: false,
          lmsActive: false,
          accessStatus: 'signup_complete',
          courseId: '',
          intake: '',
          pendingCourseId: '',
          pendingIntake: ''
        }
      });
      navigate('/student/registration');
    } catch (err) {
      if (err?.code === 'auth/email-already-in-use') {
        setError('That email is already registered. Please log in instead.');
      } else if (err?.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (err?.code === 'auth/weak-password') {
        setError('Please choose a stronger password.');
      } else {
        setError('Unable to create your account right now. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="login-page" id="main-content" tabIndex="-1">
      <div className="login-container" style={{ maxWidth: '560px' }}>
        <div className="login-logo">
          <h1 className="login-title">Student Sign Up</h1>
          <p className="login-subtitle">Create your free School of SHEM student account, then choose your course inside the student portal.</p>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          {error && (
            <div role="alert" id="register-error"
              style={{
                padding: '12px 16px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                color: 'var(--danger)',
                fontSize: '0.875rem',
                marginBottom: '20px'
              }}
            >
              {error}
            </div>
          )}

          <div className="register-form-grid">
            <div className="form-group">
              <label className="form-label" htmlFor="register-first-name">Full Name</label>
              <input id="register-first-name" className="form-input" value={form.fullName} onChange={handleChange('fullName')} placeholder="First name" autoComplete="given-name" aria-describedby={error ? 'register-error' : undefined} required />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="register-last-name">Surname</label>
              <input id="register-last-name" className="form-input" value={form.surname} onChange={handleChange('surname')} placeholder="Last name" autoComplete="family-name" aria-describedby={error ? 'register-error' : undefined} required />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="register-phone">Phone Number</label>
              <input id="register-phone" className="form-input" type="tel" value={form.phone} onChange={handleChange('phone')} placeholder="+27 ..." autoComplete="tel" aria-describedby={error ? 'register-error' : undefined} required />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="register-email">Email Address</label>
              <input id="register-email" className="form-input" type="email" value={form.email} onChange={handleChange('email')} placeholder="name@email.com" autoComplete="email" aria-describedby={error ? 'register-error' : undefined} required />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="register-password">Password</label>
              <input id="register-password" className="form-input" type="password" value={form.password} onChange={handleChange('password')} placeholder="Create a password" autoComplete="new-password" aria-describedby={error ? 'register-error' : undefined} required />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="register-confirm-password">Confirm Password</label>
              <input id="register-confirm-password" className="form-input" type="password" value={form.confirmPassword} onChange={handleChange('confirmPassword')} placeholder="Confirm your password" autoComplete="new-password" aria-describedby={error ? 'register-error' : undefined} required />
            </div>
          </div>

          <div style={{ display: 'grid', gap: '12px', marginTop: '20px' }}>
            <button type="submit" className="btn btn-primary w-full" disabled={submitting}>
              {submitting ? 'Creating account...' : 'Sign Up'}
            </button>
            <button type="button" className="btn btn-outline w-full" onClick={() => navigate('/login')}>
              Already have an account? Login
            </button>
            <button type="button" className="btn btn-secondary w-full" onClick={() => navigate('/')}>
              Back to Home
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}
